"use client";

/**
 * Sidebar con elementi multimediali di un evento.
 *
 * Mostra una lista compatta e ben visibile degli allegati V13 raggruppati
 * per tipo. Ogni voce:
 *  - Video: thumbnail YouTube se disponibile, altrimenti icona colorata →
 *    click apre l'ancora nella pagina (#media-{id}) dove il video è embedded.
 *  - Foto/Gallery: mini grid di anteprime (max 4) → click ancora alla sezione.
 *  - PDF/Slide/Note: card compatta con icona + titolo → link diretto al file.
 *
 * È pensata per essere un "indice media" rapido nella sidebar della pagina
 * dettaglio evento, complementare a <EventoMediaSections>.
 */

import Image from "next/image"; // Perf: ottimizzazione thumb sidebar (YT + foto)
import type { EventoMediaItemDto } from "@/lib/api";
import { resolveMediaUrl } from "@/lib/media-url";
import { youtubeThumbnail } from "@/lib/youtube";
import { MEDIA_TYPE_META } from "@/lib/evento-media";

function VideoCard({ item }: { item: EventoMediaItemDto }) {
  const isUrl = item.mediaType === "VIDEO_URL" && item.externalUrl;
  const yt = isUrl ? youtubeThumbnail(item.externalUrl!, "mq") : null;
  const anchor = `#media-${item.id}`;
  return (
    <a
      href={anchor}
      className="block group rounded-lg overflow-hidden bg-neutral-900 ring-1 ring-neutral-200 hover:ring-[#4a90c4] transition-all"
    >
      <div className="relative aspect-video">
        {yt ? (
          /* Perf: aspect-video fisso → Image fill, sidebar ~320px */
          <Image
            src={yt}
            alt={item.title || ""}
            fill
            sizes="320px"
            className="object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-red-500/40 to-red-900/40 flex items-center justify-center">
            <span className="text-4xl">🎬</span>
          </div>
        )}
        <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors flex items-center justify-center">
          <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#1a3a5c] ml-0.5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M8 5v14l11-7z" />
            </svg>
          </div>
        </div>
      </div>
      {item.title && (
        <div className="px-2.5 py-1.5 text-[11px] font-medium text-white bg-black/60 line-clamp-1">{item.title}</div>
      )}
    </a>
  );
}

function PhotoGrid({ items }: { items: EventoMediaItemDto[] }) {
  if (items.length === 0) return null;
  const visible = items.slice(0, 4);
  const extra = items.length - visible.length;
  return (
    <a href="#media-foto" className="block group">
      <div className="grid grid-cols-2 gap-1">
        {visible.map((img, i) => {
          const url = resolveMediaUrl(img.media);
          if (!url) return null;
          return (
            <div key={img.id} className="aspect-square overflow-hidden rounded bg-neutral-100 relative">
              {/* Perf: aspect-square fisso → Image fill (mini-grid 2 col sidebar ~160px) */}
              <Image
                src={url}
                alt={img.title || ""}
                fill
                sizes="160px"
                className="object-cover group-hover:scale-105 transition-transform"
              />
              {i === visible.length - 1 && extra > 0 && (
                <div className="absolute inset-0 bg-black/60 flex items-center justify-center text-white font-bold text-sm">
                  +{extra}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </a>
  );
}

function DocumentRow({ item }: { item: EventoMediaItemDto }) {
  const url = resolveMediaUrl(item.media);
  if (!url) return null;
  const meta = MEDIA_TYPE_META[item.mediaType];
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={`flex items-center gap-2 px-2.5 py-2 rounded-lg border text-xs hover:shadow-sm transition-shadow ${meta.pill}`}
    >
      <span className="text-lg flex-shrink-0" aria-hidden>{meta.icon}</span>
      <span className="flex-1 min-w-0 font-medium truncate">
        {item.title || item.media?.filename || meta.label}
      </span>
      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 opacity-60 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
      </svg>
    </a>
  );
}

/**
 * Riga per un allegato di rassegna stampa. Sfrutta externalUrl per PRESS_URL
 * e il media caricato per PRESS_FILE, mostrando l'hostname come hint.
 */
function PressRow({ item }: { item: EventoMediaItemDto }) {
  const fileUrl = resolveMediaUrl(item.media);
  const href = item.mediaType === "PRESS_URL" ? item.externalUrl : fileUrl;
  if (!href) return null;
  const meta = MEDIA_TYPE_META[item.mediaType];
  let hostname: string | null = null;
  if (item.mediaType === "PRESS_URL" && item.externalUrl) {
    try { hostname = new URL(item.externalUrl).hostname.replace(/^www\./, ""); } catch { /* ignore */ }
  }
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={`flex items-center gap-2 px-2.5 py-2 rounded-lg border text-xs hover:shadow-sm transition-shadow ${meta.pill}`}
    >
      <span className="text-lg flex-shrink-0" aria-hidden>{meta.icon}</span>
      <span className="flex-1 min-w-0">
        <span className="block font-medium truncate">
          {item.title || item.media?.filename || meta.label}
        </span>
        {hostname && <span className="block text-[10px] opacity-70 truncate">{hostname}</span>}
      </span>
      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 opacity-60 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
      </svg>
    </a>
  );
}

export function EventoMediaSidebar({ items }: { items: EventoMediaItemDto[] | null | undefined }) {
  if (!items?.length) return null;

  const sorted = [...items].sort((a, b) => a.position - b.position);
  const videos = sorted.filter((i) => i.mediaType === "VIDEO_URL" || i.mediaType === "VIDEO_FILE");
  const photos = sorted.filter((i) => i.mediaType === "PHOTO" || i.mediaType === "GALLERY");
  const docs = sorted.filter((i) => i.mediaType === "PDF" || i.mediaType === "SLIDE" || i.mediaType === "NOTE");
  const press = sorted.filter((i) => i.mediaType === "PRESS_URL" || i.mediaType === "PRESS_FILE");

  const total = videos.length + photos.length + docs.length + press.length;

  return (
    <div className="rounded-xl border border-neutral-200 bg-white overflow-hidden">
      <div className="px-4 py-3 bg-gradient-to-r from-[#1a3a5c] to-[#4a90c4]">
        <h3 className="text-sm font-bold text-white flex items-center gap-2">
          <span>📎</span>
          <span>Contenuti multimediali</span>
        </h3>
        <p className="text-[10px] text-white/70 mt-0.5">{total} {total === 1 ? "allegato" : "allegati"}</p>
      </div>

      <div className="p-3 space-y-3">
        {videos.length > 0 && (
          <div className="space-y-2">
            {/* A11y: text-neutral-400 → text-neutral-500 (WCAG AA su bg-white) */}
            <span className="block text-[10px] font-bold uppercase tracking-wider text-neutral-500">
              🎬 Video ({videos.length})
            </span>
            {videos.map((v) => <VideoCard key={v.id} item={v} />)}
          </div>
        )}

        {photos.length > 0 && (
          <div className="space-y-2">
            <span className="block text-[10px] font-bold uppercase tracking-wider text-neutral-500">
              🖼 Foto ({photos.length})
            </span>
            <PhotoGrid items={photos} />
          </div>
        )}

        {docs.length > 0 && (
          <div className="space-y-1.5">
            <span className="block text-[10px] font-bold uppercase tracking-wider text-neutral-500">
              📚 Materiali ({docs.length})
            </span>
            {docs.map((d) => <DocumentRow key={d.id} item={d} />)}
          </div>
        )}

        {press.length > 0 && (
          <div className="space-y-1.5">
            <span className="block text-[10px] font-bold uppercase tracking-wider text-neutral-500">
              📰 Rassegna stampa ({press.length})
            </span>
            {press.map((p) => <PressRow key={p.id} item={p} />)}
          </div>
        )}
      </div>
    </div>
  );
}

